import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, coverUrl, previewUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Star, Check, FileText, Languages, Tag, ShoppingCart, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [previewPage, setPreviewPage] = useState(0);
  const [owned, setOwned] = useState(false);
  const { user } = useAuth();
  const { cart, add } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [busyPaypal, setBusyPaypal] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, body: "" });

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data));
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data));
  }, [id]);

  useEffect(() => {
    if (user) api.get("/library").then((r) => setOwned(r.data.items.some((i) => i.id === id)));
  }, [user, id]);

  const inCart = cart?.items?.some((i) => i.id === id);

  const onBuyNow = async () => {
    if (!user) { navigate(`/login?next=/product/${id}`); return; }
    if (owned) { navigate("/library"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/checkout/buy/${id}`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); } finally { setBusy(false); }
  };

  const onPaypal = async () => {
    if (!user) { navigate(`/login?next=/product/${id}`); return; }
    if (owned) { navigate("/library"); return; }
    setBusyPaypal(true);
    try {
      const { data } = await api.post("/checkout/paypal/create", { origin_url: window.location.origin, product_id: id });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(e.response?.data?.detail || "PayPal checkout failed"); } finally { setBusyPaypal(false); }
  };

  const onAddToCart = async () => {
    if (!user) { navigate(`/login?next=/product/${id}`); return; }
    if (owned) { toast.info("Already in your library"); return; }
    try { await add(id); toast.success("Added to cart"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/products/${id}/reviews`, newReview);
      setReviews((await api.get(`/products/${id}/reviews`)).data);
      setP((await api.get(`/products/${id}`)).data);
      setNewReview({ rating: 5, body: "" });
      toast.success("Review posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post review"); }
  };

  if (!p) return <div className="container-page py-16 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="container-page py-10" data-testid="product-detail">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3">
          <div className="aspect-[2/3] w-full rounded-sm border border-accent/30 bg-muted overflow-hidden shadow-[0_20px_50px_-25px_rgba(58,31,71,0.5)]">
            <img src={coverUrl(p)} alt={`Cover of ${p.title}`} className="object-cover w-full h-full" />
          </div>
        </div>

        <div className="lg:col-span-6 flex flex-col gap-5">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Soft Spine Press</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2 text-balance">{p.title}</h1>
            {p.subtitle && <div className="text-base text-muted-foreground mt-2 italic font-display">{p.subtitle}</div>}
          </div>

          <div className="flex items-center gap-4 text-sm">
            {p.rating_count > 0 ? (
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className={`h-4 w-4 ${i <= Math.round(p.rating_avg) ? "fill-accent text-accent" : "text-muted-foreground/30"}`} strokeWidth={1.5} />
                ))}
                <span className="font-medium ml-1">{p.rating_avg.toFixed(1)}</span>
                <span className="text-muted-foreground">({p.rating_count} reviews)</span>
              </div>
            ) : <span className="text-muted-foreground">Not yet reviewed</span>}
            <Separator orientation="vertical" className="h-4" />
            <span className="text-muted-foreground">{p.units_sold} sold</span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-sm font-normal"><FileText className="h-3 w-3 mr-1" strokeWidth={1.5} />{p.page_count} pages</Badge>
            <Badge variant="secondary" className="rounded-sm font-normal"><Languages className="h-3 w-3 mr-1" strokeWidth={1.5} />{p.language}</Badge>
            {p.keywords?.slice(0, 5).map((k) => (
              <Badge key={k} variant="outline" className="rounded-sm font-normal"><Tag className="h-3 w-3 mr-1" strokeWidth={1.5} />{k}</Badge>
            ))}
          </div>

          <p className="text-sm leading-[1.8] text-muted-foreground whitespace-pre-line">{p.description}</p>

          {p.preview_pages_count > 0 && (
            <div className="mt-6" data-testid="product-preview">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-3">Sample preview</div>
              <div className="border border-border rounded-sm bg-muted overflow-hidden">
                <img src={previewUrl(p.id, previewPage)} alt={`Preview page ${previewPage + 1}`} className="w-full object-contain max-h-[520px]" />
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex gap-1.5">
                  {Array.from({ length: p.preview_pages_count }).map((_, i) => (
                    <button key={i} onClick={() => setPreviewPage(i)} data-testid={`preview-page-${i}`}
                      className={`text-xs px-3 py-1 rounded-sm border ${previewPage === i ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      Page {i + 1}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground italic">Watermarked sample.</div>
              </div>
            </div>
          )}

          <div className="mt-10" data-testid="reviews-section">
            <h2 className="font-display text-3xl tracking-tight mb-4 border-b border-border pb-2">Reader reviews</h2>
            {owned && !reviews.find((r) => r.user_id === user?.user_id) && (
              <div className="border border-border rounded-sm p-4 mb-6 bg-card">
                <div className="text-sm font-medium mb-2">Write a review</div>
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((i) => (
                    <button key={i} onClick={() => setNewReview({ ...newReview, rating: i })} data-testid={`rating-${i}`}>
                      <Star className={`h-5 w-5 ${i <= newReview.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
                <textarea value={newReview.body} onChange={(e) => setNewReview({ ...newReview, body: e.target.value })}
                  placeholder="What stood out to you?" className="w-full text-sm border border-border rounded-sm p-3 outline-none focus:border-primary bg-background"
                  rows={3} data-testid="review-body" />
                <Button onClick={submitReview} className="mt-2 rounded-sm" size="sm" data-testid="submit-review">Post review</Button>
              </div>
            )}
            <div className="space-y-5">
              {reviews.length === 0 && <div className="text-sm text-muted-foreground italic">Be the first to share your thoughts.</div>}
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-border pb-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{r.user_name}</span>
                    {r.verified && <Badge variant="outline" className="rounded-sm text-[10px] font-normal"><Check className="h-2.5 w-2.5 mr-1" strokeWidth={2} />Verified purchase</Badge>}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {[1,2,3,4,5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} strokeWidth={1.5} />)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-3">
          <div className="border border-border rounded-sm p-6 bg-card sticky top-32" data-testid="buy-panel">
            <div className="text-3xl font-semibold">A${p.price.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground mt-1 italic">Digital download — instant access</div>
            <Separator className="my-4" />
            {owned ? (
              <Button asChild className="w-full rounded-sm" data-testid="goto-library">
                <Link to="/library"><Check className="h-4 w-4 mr-2" strokeWidth={2} /> Owned — Go to library</Link>
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <Button onClick={onBuyNow} disabled={busy || busyPaypal} className="w-full rounded-sm uppercase tracking-wide" data-testid="buy-now">
                  <Zap className="h-4 w-4 mr-2" strokeWidth={1.5} /> Pay with card
                </Button>
                <Button onClick={onPaypal} disabled={busy || busyPaypal} variant="outline" className="w-full rounded-sm uppercase tracking-wide border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white" data-testid="buy-paypal">
                  {busyPaypal ? "Redirecting…" : "Pay with PayPal"}
                </Button>
                <Button onClick={onAddToCart} variant="ghost" disabled={inCart || busy || busyPaypal} className="w-full rounded-sm uppercase tracking-wide" data-testid="add-to-cart">
                  <ShoppingCart className="h-4 w-4 mr-2" strokeWidth={1.5} /> {inCart ? "In cart" : "Add to cart"}
                </Button>
              </div>
            )}
            <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-accent" strokeWidth={2} /> Lifetime access in your library</li>
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-accent" strokeWidth={2} /> Personalised watermark</li>
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-accent" strokeWidth={2} /> Faulty files fixed or replaced</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
